import { Component, OnDestroy, OnInit } from '@angular/core';
import { App, AppInfo } from '@capacitor/app';
import { AuthService } from './services/core/auth.service';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { User } from './interfaces/core/user';
import { environment } from '../environments/environment';
import { SyncService } from './services/core/sync.service';
import { db } from './tables/db.service';
import { TimeoutHelper } from './helpers/timeout-helper';
import { ErrorMessagesService } from './services/core/error-messages.service';
import {PluginListenerHandle} from '@capacitor/core';
import {SwUpdate} from '@angular/service-worker';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  protected user: User;
  protected appInfo: AppInfo = null;
  protected environment = environment;

  private listeners: PluginListenerHandle[] = [];
  private readonly WHITE_SCREEN_RELOAD_KEY = 'white_screen_reload_ts';
  private readonly WHITE_SCREEN_COOLDOWN_MS = 60_000; // 1 minute cooldown to prevent reload loops
  private readonly UPDATE_RELOAD_KEY = 'update_reload_ts';
  private readonly UPDATE_RELOAD_COOLDOWN_MS = 60_000;
  private updateCheckIntervalId: number | null = null;

  constructor(
    private router: Router,
    private errorMessagesService: ErrorMessagesService,
    private authService: AuthService,
    protected syncService: SyncService,
    private swUpdate: SwUpdate
  ) {}

  public async ngOnInit(): Promise<void> {
    // Disable back button behavior (intentionally empty handler).
    this.listeners.push(await App.addListener('backButton', () => {}));

    this.listeners.push(
      await App.addListener('pause', async () => {
        console.log('App pause');
      })
    );

    this.listeners.push(
      await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          setTimeout(() => {
            void this.checkForWhiteScreen();
          }, 500);
        }
      })
    );

    this.listeners.push(
      await App.addListener('resume', async () => {
        console.log('App resume');

        try {
          // Best effort DB reconnect on resume.
          try {
            await Promise.resolve(db.close());
          } catch (e) {
            console.warn('DB close failed (continuing):', e);
          }

          await this.retryDbOpen(3, 1000);
        } catch {
          await this.errorMessagesService.showCustomErrorMessage(
            'Datenbank',
            'Fehler beim öffnen der Datenbank'
          );
        }

        void this.checkAuthToken();
      })
    );

    App.getInfo()
      .then((info: AppInfo) => {
        this.appInfo = info;
      })
      .catch(() => {
        console.info('App info not available');
      });

    this.setupWebUpdates();
    void this.checkAuthToken();
  }

  public async ngOnDestroy(): Promise<void> {
    // Remove Capacitor listeners to avoid leaks.
    await Promise.allSettled(this.listeners.map(l => l.remove()));
    this.listeners = [];

    if (this.updateCheckIntervalId !== null) {
      clearInterval(this.updateCheckIntervalId);
      this.updateCheckIntervalId = null;
    }
  }

  private async retryDbOpen(maxRetries: number, delay: number): Promise<void> {
    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        await db.open();
        console.log('Database connection re-established.');
        return;
      } catch (error) {
        attempt++;
        console.error(`Error while opening db connection. Attempt ${attempt} of ${maxRetries}`, error);

        if (attempt < maxRetries) {
          await TimeoutHelper.sleep(delay);
          delay *= 2;
        } else {
          console.error('Max retries reached. DB connection could not be re-established.');
          throw error;
        }
      }
    }
  }

  private async checkAuthToken(): Promise<void> {
    try {
      const authToken = AuthService.getAuthToken();
      if (!authToken) return;

      if (DateTime.fromISO(authToken.expires).diffNow().as('seconds') < 0) {
        await this.authService.unlockLogin();
        if (this.router.url !== '/login') {
          await this.router.navigate(['/login']);
        }
      }
    } catch (e) {
      console.error('Error while checking auth token:', e);
    }
  }

  private shouldReloadNow(): boolean {
    try {
      const now = Date.now();
      const last = Number(localStorage.getItem(this.WHITE_SCREEN_RELOAD_KEY) || '0');
      if (now - last < this.WHITE_SCREEN_COOLDOWN_MS) return false;
      localStorage.setItem(this.WHITE_SCREEN_RELOAD_KEY, String(now));
      return true;
    } catch {
      // If localStorage is unavailable, allow a reload (best effort).
      return true;
    }
  }

  private shouldReloadForUpdateNow(): boolean {
    try {
      const now = Date.now();
      const last = Number(localStorage.getItem(this.UPDATE_RELOAD_KEY) || '0');
      if (now - last < this.UPDATE_RELOAD_COOLDOWN_MS) return false;
      localStorage.setItem(this.UPDATE_RELOAD_KEY, String(now));
      return true;
    } catch {
      return true;
    }
  }

  private setupWebUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.subscribe(event => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          console.log('New app version detected:', event.version.hash);
          break;
        case 'VERSION_READY':
          console.log('New app version ready, reloading...');
          this.reloadForUpdate();
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error('App update installation failed:', event.error);
          break;
      }
    });

    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Unrecoverable app state detected:', event.reason);
      this.reloadForUpdate();
    });

    window.setTimeout(() => {
      void this.checkForAppUpdate();
    }, 35_000);

    this.updateCheckIntervalId = window.setInterval(() => {
      void this.checkForAppUpdate();
    }, 5 * 60_000);
  }

  private async checkForAppUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    try {
      const hasUpdate = await this.swUpdate.checkForUpdate();
      if (hasUpdate) {
        console.log('App update downloaded, reloading...');
        this.reloadForUpdate();
      }
    } catch (error) {
      console.error('App update check failed:', error);
    }
  }

  private reloadForUpdate(): void {
    if (!this.shouldReloadForUpdateNow()) {
      return;
    }

    setTimeout(() => {
      window.location.reload();
    }, 300);
  }

  private async checkForWhiteScreen(): Promise<void> {
    try {
      // Prevent reload loops.
      if (!this.shouldReloadNow()) return;

      const outlet = document.querySelector('ion-router-outlet');
      const hasOutlet = !!outlet;
      const outletHasChildren = (outlet?.children?.length || 0) > 0;

      // If there is no router outlet or it has no rendered children, it might be a blank UI.
      // Keep the heuristic conservative to avoid false positives with Ionic Shadow DOM.
      if (!hasOutlet || !outletHasChildren) {
        console.warn('Potential white screen detected, reloading...');
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (e) {
      console.error('Error checking for white screen:', e);
    }
  }
}
